import type { FormInstance } from 'antd';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { compareObjects } from '@/helpers/object.helper';
import { useCommonStore } from '@/store/common.store';

function removeIPLabelProperties(data: Record<string, unknown>) {
  for (const [key, value] of Object.entries(data)) {
    const isFalseCheck = key.startsWith('check-') && value === false;
    const isEmptyDate = key.startsWith('date-') && !value;
    if (isFalseCheck || isEmptyDate) delete data[key];
  }
  return data;
}

export function useDetectFormValuesChanged(props?: {
  form: FormInstance;
  isIpLabel?: boolean;
  isOrganizationUpdate?: boolean;
  initialValues?: unknown;
}) {
  const [internalInitialValues, setInternalInitialValues] = useState();
  const { form, initialValues, isIpLabel, isOrganizationUpdate } = props ?? {};
  const { isFormUpdated, setIsFormChanged, setIsFormUpdated } = useCommonStore(
    useShallow(({ isFormUpdated, setIsFormChanged, setIsFormUpdated }) => ({
      isFormUpdated,
      setIsFormChanged,
      setIsFormUpdated,
    })),
  );

  function handleFieldsChange() {
    const isFormChanged = !compareObjects(
      internalInitialValues ?? initialValues,
      isIpLabel
        ? removeIPLabelProperties(form?.getFieldsValue(true))
        : isOrganizationUpdate
          ? form?.getFieldsValue(true)
          : form?.getFieldsValue(),
    );
    setIsFormChanged(isFormChanged);
  }

  function handleFieldsChangeWithExtras(extras?: Record<string, unknown>) {
    const isFormChanged = !compareObjects(
      internalInitialValues ?? initialValues,
      { ...form?.getFieldsValue(), ...extras },
    );
    setIsFormChanged(isFormChanged);
  }

  useEffect(() => {
    if (isFormUpdated) {
      setInternalInitialValues(
        isIpLabel
          ? removeIPLabelProperties(form?.getFieldsValue(true))
          : isOrganizationUpdate
            ? form?.getFieldsValue(true)
            : form?.getFieldsValue(),
      );
      setIsFormUpdated(false);
    }
  }, [form, isFormUpdated, setIsFormUpdated, isIpLabel, isOrganizationUpdate]);

  return { handleFieldsChange, handleFieldsChangeWithExtras };
}
